import { cn, Container, Section } from '@resolute/ui';
import { ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Reveal } from '@/components/motion/reveal';
import { PageHero } from '@/components/page-hero';
import { Seo } from '@/lib/seo';

const TERMS_EMAIL = 'ronak@resoluteproperty.in';

const IMPORTANT_NOTICE =
  'Please read carefully: By using our services or website, you agree to be bound by the following terms and conditions.';

interface TermsSection {
  id: string;
  heading: string;
  paragraphs: string[];
}

const SECTIONS: TermsSection[] = [
  {
    id: 'sms-content-and-liability',
    heading: 'SMS content and liability',
    paragraphs: [
      'We do not take any responsibility on the Materials / Market / Any type of facts of SMS Contains Which are transmitted from our server. We are transmitted the SMS only.',
    ],
  },
  {
    id: 'force-majeure',
    heading: 'Force majeure',
    paragraphs: [
      'Neither party will be liable for failure to perform any of its obligations, which are beyond its reasonable control.',
    ],
  },
  {
    id: 'how-we-use-your-information',
    heading: 'How we use your information',
    paragraphs: [
      'We may use your information to respond to your inquiries, provide customer service support, send you important information about the services, and send you marketing communications (with your consent) via different channels, including but not limited to SMS, Email, RCS, WhatsApp, and Voice.',
    ],
  },
  {
    id: 'message-content-standards',
    heading: 'Message content standards',
    paragraphs: [
      'We send message only for information to our members. Message is approved by TRAI / Operator side as per your rules. We will not send any type of DENIGRATE / SPAM / FRAUD / CRITICIZE / INSULT THEM / DAMAGING THEIR REPUTATION / ILLEGAL text contain in Message.',
    ],
  },
  {
    id: 'message-delivery',
    heading: 'Message delivery',
    paragraphs: [
      'Every effort will be made to transmit the message at the earliest. However, Resolute Property Solution will not be responsible if messages are transmitted delay due to Technical Error or Transmitted repetitively. We will have no rights to raise the dispute on this issue.',
    ],
  },
  {
    id: 'natural-calamity',
    heading: 'Natural calamity',
    paragraphs: [
      'Resolute Property Solution Company will not be liable for failure to perform any of its obligations during any period in which such performance is delayed by any kind of Natural Calamity/Disaster beyond its reasonable control.',
    ],
  },
  {
    id: 'governing-jurisdiction',
    heading: 'Governing jurisdiction',
    paragraphs: [
      'The courts at Ahmedabad only shall have the jurisdiction in enforcing the provisions of this agreement.',
    ],
  },
  {
    id: 'use-of-your-number',
    heading: 'Use of your number',
    paragraphs: [
      'We are using your number for our internal promotion purpose and communications through different media.',
    ],
  },
];

/** Scroll-spy: highlights whichever section's heading is nearest the top of
 *  the viewport in the sticky table of contents. */
function useActiveSection(ids: string[]): string {
  const [active, setActive] = useState(ids[0]!);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-112px 0px -70% 0px', threshold: 0 },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);

  return active;
}

export default function TermsPage() {
  const ids = SECTIONS.map((s) => s.id);
  const activeId = useActiveSection(ids);

  return (
    <>
      <Seo
        title="Terms of Use"
        description="The terms that govern how Resolute Property Solutions communicates with you — SMS, marketing messages, liability, and jurisdiction."
        path="/terms"
      />

      <PageHero
        eyebrow="Legal"
        title="Terms of Use."
        titleLine2="The rules of the road."
        description="The terms that apply when you use this site or our services — including how we communicate with you."
      />

      <Section>
        <Container>
          {/* No max-w cap here (unlike an earlier version) — a narrower cap on
              this grid left the whole two-column block stranded well short of
              Container's actual 1440px width, wasting space that never reached
              the content column. Matches Search/Property-Detail's grids, which
              also rely on Container alone. The content column's own max-w-4xl
              below is the deliberate readability cap on paragraph line length. */}
          <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
            {/* Table of contents — sticky, scroll-spy highlighted */}
            <aside className="hidden lg:block">
              <nav className="lg:sticky lg:top-[calc(var(--header-offset)+1.5rem)]" aria-label="Terms of Use sections">
                <p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  On this page
                </p>
                <ul className="mt-3 flex flex-col gap-0.5 border-l border-border">
                  {SECTIONS.map((s, i) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={cn(
                          '-ml-px block border-l-2 py-1.5 pl-3 text-sm transition-colors duration-200 ease-out-expo',
                          activeId === s.id
                            ? 'border-primary font-medium text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {i + 1}. {s.heading}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            {/* Content — max-w-4xl caps paragraph line length for readability;
                narrower than the grid's actual 1fr track (now that the outer
                cap above is gone), so this is a deliberate reading-width
                choice, not an accidental leftover squeeze. */}
            <div className="max-w-4xl">
              <Reveal className="mb-10 flex gap-3 rounded-lg border border-primary/25 bg-primary/5 p-5">
                <ShieldAlert className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-foreground">{IMPORTANT_NOTICE}</p>
              </Reveal>

              {/* Each section reveals independently as it's scrolled to — not a
                  manual index-proportional delay (which only "staggers" the
                  first screenful; sections further down would already be
                  fully revealed by the time they're actually scrolled into
                  view, since Reveal fires on ITS OWN viewport entry). */}
              <div className="space-y-10">
                {SECTIONS.map((s, i) => (
                  <div key={s.id} id={s.id} className="scroll-mt-24">
                    <Reveal>
                      <div className="flex items-start gap-3">
                        <span className="flex size-8 shrink-0 items-center justify-center rounded border border-border bg-card text-sm font-semibold text-muted-foreground">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <h2 className="pt-0.5 text-lg font-semibold tracking-tight">{s.heading}</h2>
                      </div>
                      <div className="mt-3 space-y-3 pl-11">
                        {s.paragraphs.map((p) => (
                          <p key={p.slice(0, 40)} className="leading-relaxed text-muted-foreground">
                            {p}
                          </p>
                        ))}
                      </div>
                    </Reveal>
                  </div>
                ))}
              </div>

              <Reveal className="mt-10 rounded-lg border border-border bg-card p-6">
                <h2 className="text-lg font-semibold">Questions?</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Email{' '}
                  <a
                    href={`mailto:${TERMS_EMAIL}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {TERMS_EMAIL}
                  </a>{' '}
                  and we&apos;ll help.
                </p>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
